import { Input } from '@material-tailwind/react'
import { LoadingSpinner } from 'components/LoadingSpinner'
import { useFormik } from 'formik'
import Cookies from 'js-cookie'
import { useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import { PublicRoute } from 'routers/PublicRoute'
import { signIn } from 'services/sign-in'
import ButtonPrimary from 'shared/Button/ButtonPrimary'
import InputTemplate from 'shared/Input/Input'
import { NoSearchEngineTemplate } from 'templates/NoSearchEngineTemplate'

import { ReactComponent as EyeOpen } from '../../images/eye.svg'
import { ReactComponent as EyeClosed } from '../../images/eyeClosed.svg'
import { validationSchema } from './schema'
import { handleError } from './util'

export interface PageLoginProps {
  className?: string
}

const PageLogin = ({ className = '' }) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({
    password: false,
    confirmNewPassword: false,
  })

  const handleClickShowPassword = (field: string) => {
    setShowPassword((prevShowPassword) => ({
      ...prevShowPassword,
      [field]: !prevShowPassword[field],
    }))
  }

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      setIsLoading(true)
      const { accessToken, expiresIn, refreshToken } = await signIn(values)

      const expirationDate = new Date()
      expirationDate.setSeconds(expirationDate.getSeconds() + expiresIn)

      Cookies.set('auth_token', accessToken, {
        secure: window.location.protocol === 'https:',
        expires: expirationDate,
        sameSite: window.location.protocol === 'https:' ? 'strict' : 'Lax',
        path: '/',
      })

      Cookies.set('refresh_token', refreshToken, {
        secure: window.location.protocol === 'https:',
        sameSite: window.location.protocol === 'https:' ? 'strict' : 'Lax',
        path: '/',
      })

      toast.success('Bem-vindo! Redirecionando...')
      navigate('/home')
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Failed to fetch') {
          toast.error('Erro ao tentar conectar com o servidor.')
          return 0
        }

        toast.error(error.message)
      } else {
        toast.error('Um erro inesperado ocorreu. Tente novamente mais tarde.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  })

  return (
    <PublicRoute>
      <NoSearchEngineTemplate>
        <div className={`nc-PageLogin ${className}`} data-nc-id="PageLogin">
          <Toaster position="top-center" />
          <div className="container mb-24 lg:mb-32">
            <h2 className="my-[51px] flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
              Empresas
            </h2>
            <div className="max-w-md mx-auto space-y-6">
              <form
                className="grid grid-cols-1 gap-6"
                action="#"
                method="post"
                onSubmit={formik.handleSubmit}
              >
                <label className="block">
                  <span className="text-neutral-800 dark:text-neutral-200">
                    Endereço de e-mail
                  </span>
                  <InputTemplate
                    type="email"
                    placeholder="example@example.com"
                    className={handleError('email', formik)}
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.email && formik.touched.email && (
                    <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs">
                      {formik.errors.email}
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                    Senha
                  </span>
                  <div className="flex flex-col items-start mb-1">
                    <Input
                      icon={
                        showPassword['password'] ? (
                          <EyeClosed
                            onClick={() => handleClickShowPassword('password')}
                            className="mt-3 cursor-pointer"
                          />
                        ) : (
                          <EyeOpen
                            onClick={() => handleClickShowPassword('password')}
                            className="mt-3 cursor-pointer"
                          />
                        )
                      }
                      id="password"
                      name="password"
                      type={showPassword['password'] ? 'text' : 'password'}
                      className={handleError('password', formik)}
                      placeholder="Sua senha"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      crossOrigin={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    {formik.errors.password && formik.touched.password && (
                      <span className="text-[#d32f2f] mt-2 mr-4 mb-0 ml-3 text-left text-xs items-start">
                        {formik.errors.password}
                      </span>
                    )}
                  </div>
                </label>

                <div className="w-full text-right p-2">
                  <Link
                    to="/forgot-password"
                    className="text-sm inline-block p-2"
                  >
                    Esqueceu sua senha?
                  </Link>
                </div>

                <ButtonPrimary
                  className="bg-[--blue] hover:bg-[--blue-dark] disabled:bg-[#b2d0e0] disabled:cursor-not-allowed"
                  type="submit"
                  disabled={!formik.isValid}
                >
                  {isLoading ? <LoadingSpinner /> : 'Entrar'}
                </ButtonPrimary>
              </form>
            </div>
          </div>
        </div>
      </NoSearchEngineTemplate>
    </PublicRoute>
  )
}

export default PageLogin
